import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const Roles = () => {
  const { user } = useContext(AuthContext);
  const [roles, setRoles] = useState([]);
  const [defaultRoles, setDefaultRoles] = useState([]);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [expandedRole, setExpandedRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    base_role: 'operator',
    permissions: {}
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, defaultsRes] = await Promise.all([
        axios.get(`${API}/roles`),
        axios.get(`${API}/roles/defaults/list`)
      ]);
      setRoles(rolesRes.data);
      setDefaultRoles(defaultsRes.data.roles);
      setModules(defaultsRes.data.modules);
    } catch (error) {
      toast.error('Error al cargar roles');
    } finally {
      setLoading(false);
    }
  };

  const handleBaseRoleChange = (baseRole) => {
    const basePermissions = defaultRoles.find(r => r.id === baseRole)?.permissions || {};
    setFormData({
      ...formData,
      base_role: baseRole,
      permissions: { ...basePermissions }
    });
  };

  const handlePermissionChange = (module, action, checked) => {
    const modulePerms = formData.permissions[module] || [];
    let newPerms;
    
    if (checked) {
      newPerms = [...modulePerms, action];
    } else {
      newPerms = modulePerms.filter(p => p !== action);
    }
    
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [module]: newPerms
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await axios.put(`${API}/roles/${editingRole.id}`, formData);
        toast.success('Rol actualizado');
      } else {
        await axios.post(`${API}/roles`, formData);
        toast.success('Rol creado');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este rol?')) return;
    try {
      await axios.delete(`${API}/roles/${id}`);
      toast.success('Rol eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      base_role: 'operator',
      permissions: {}
    });
    setEditingRole(null);
  };

  const openEditDialog = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      base_role: role.base_role,
      permissions: role.permissions || {}
    });
    setDialogOpen(true);
  };

  const getRoleBadgeColor = (baseRole) => {
    const colors = {
      admin: 'bg-red-100 text-red-700',
      supervisor: 'bg-blue-100 text-blue-700',
      maintenance_manager: 'bg-orange-100 text-orange-700',
      operator: 'bg-green-100 text-green-700',
      management: 'bg-purple-100 text-purple-700'
    };
    return colors[baseRole] || 'bg-slate-100 text-slate-700';
  };

  const getActionLabel = (action) => {
    const labels = {
      view: 'Ver',
      create: 'Crear',
      edit: 'Editar',
      delete: 'Eliminar',
      export: 'Exportar',
      approve: 'Aprobar',
      restore: 'Restaurar'
    };
    return labels[action] || action;
  };

  const allActions = ['view', 'create', 'edit', 'delete', 'export', 'approve', 'restore'];

  return (
    <div data-testid="roles-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Roles y Permisos
          </h1>
          <p className="text-slate-600 mt-2">Configurar permisos por módulo</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-role-button" className="btn-primary">
              <Plus size={20} className="mr-2" />
              Nuevo Rol
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {editingRole ? 'Editar Rol' : 'Nuevo Rol'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nombre del Rol *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-field"
                    placeholder="Ej: Supervisor de Campo"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Rol Base *</label>
                  <select
                    value={formData.base_role}
                    onChange={(e) => handleBaseRoleChange(e.target.value)}
                    className="input-field"
                    required
                  >
                    {defaultRoles.map((role) => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">Los permisos se heredan del rol base</p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-field"
                    rows={2}
                    placeholder="Descripción del rol..."
                  />
                </div>
              </div>

              {/* Permissions Matrix */}
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Permisos por Módulo</h3>
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700">Módulo</th>
                        {allActions.map(action => (
                          <th key={action} className="px-3 py-3 text-center font-semibold text-slate-700">
                            {getActionLabel(action)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(modules).map(([moduleKey, moduleName]) => (
                        <tr key={moduleKey} className="border-t border-slate-100">
                          <td className="px-4 py-3 font-medium text-slate-700">{moduleName}</td>
                          {allActions.map(action => (
                            <td key={action} className="px-3 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={(formData.permissions[moduleKey] || []).includes(action)}
                                onChange={(e) => handlePermissionChange(moduleKey, action, e.target.checked)}
                                className="w-4 h-4 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                  className="btn-secondary"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="btn-primary">
                  {editingRole ? 'Actualizar' : 'Crear Rol'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Roles List */}
      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="space-y-4">
          {roles.map((role) => (
            <div
              key={role.id}
              className="bg-white border border-slate-200 rounded-md overflow-hidden"
            >
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
                onClick={() => setExpandedRole(expandedRole === role.id ? null : role.id)}
              >
                <div className="flex items-center">
                  <Shield size={20} className="text-orange-500 mr-3" />
                  <div>
                    <h3 className="font-semibold text-slate-800">{role.name}</h3>
                    {role.description && (
                      <p className="text-sm text-slate-500">{role.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`status-badge ${getRoleBadgeColor(role.base_role)}`}>
                    {defaultRoles.find(r => r.id === role.base_role)?.name || role.base_role}
                  </span>
                  {role.is_system && (
                    <span className="status-badge bg-slate-100 text-slate-600">Sistema</span>
                  )}
                  <>
  <button
    onClick={(e) => {
      e.stopPropagation();
      openEditDialog(role);
    }}
    className="p-2 hover:bg-slate-100 rounded"
    title="Editar"
  >
    <Edit size={18} className="text-orange-500" />
  </button>

  {!role.is_system && (
    <button
      onClick={(e) => {
        e.stopPropagation();
        handleDelete(role.id);
      }}
      className="p-2 hover:bg-red-50 rounded"
      title="Eliminar"
    >
      <Trash2 size={18} className="text-red-500" />
    </button>
  )}
</>
                  {expandedRole === role.id ? (
                    <ChevronUp size={20} className="text-slate-400" />
                  ) : (
                    <ChevronDown size={20} className="text-slate-400" />
                  )}
                </div>
              </div>

              {/* Expanded Permissions View */}
              {expandedRole === role.id && (
                <div className="border-t border-slate-200 p-4 bg-slate-50">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Permisos asignados:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {Object.entries(role.permissions || {}).map(([moduleKey, actions]) => (
                      actions.length > 0 && (
                        <div key={moduleKey} className="bg-white p-3 rounded border border-slate-200">
                          <p className="font-medium text-slate-800 text-sm mb-1">
                            {modules[moduleKey] || moduleKey}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {actions.map(action => (
                              <span
                                key={action}
                                className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded"
                              >
                                {getActionLabel(action)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {roles.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No hay roles configurados
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Roles;
